import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Site-wide scroll reveal. On every route it finds the blocks inside each section of <main>
// that have no animation of their own, hides them, and plays a short rise-in (see .ft-reveal
// in animations.css) as each one scrolls into view. Grids and lists reveal item by item.
// Anything already animated, by CSS or a component, is left alone so nothing animates twice.

const STAGGER_MS = 70;
const MAX_STAGGER_MS = 420;

const isAnimated = el => {
  const name = getComputedStyle(el).animationName;
  return Boolean(name) && name !== "none";
};

// True when the element, an ancestor inside <main>, or any descendant already animates.
function touchesAnimation(el, main) {
  for (let node = el; node && node !== main; node = node.parentElement) {
    if (isAnimated(node)) return true;
  }
  return [...el.querySelectorAll("*")].some(isAnimated);
}

// Grids, flex rows and lists of three or more items reveal their items one after another.
function isGroup(el) {
  if (el.children.length < 3) return false;
  if (el.tagName === "UL" || el.tagName === "OL") return true;
  const { display, flexDirection } = getComputedStyle(el);
  return display.includes("grid") || (display.includes("flex") && !flexDirection.startsWith("column"));
}

function collectTargets(main) {
  const sections = [...main.children].flatMap(child =>
    child.tagName === "SECTION" || !child.querySelector("section")
      ? [child]
      : [...child.querySelectorAll("section")].filter(s => !s.parentElement.closest("section"))
  );

  const targets = [];
  sections.forEach(section => {
    const container = section.querySelector(":scope > .ft-container, :scope > div > .ft-container") || section;
    [...container.children].forEach(block => {
      // Sticky/fixed blocks (section navs) must stay visible, and animated ones are already handled.
      if (/sticky|fixed/.test(getComputedStyle(block).position) || touchesAnimation(block, main)) return;
      if (isGroup(block)) {
        [...block.children].forEach((item, i) => targets.push([item, Math.min(i * STAGGER_MS, MAX_STAGGER_MS)]));
      } else {
        targets.push([block, 0]);
      }
    });
  });
  return targets;
}

export default function RevealOnScroll() {
  const { pathname } = useLocation();

  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || typeof IntersectionObserver === "undefined") return undefined;

    let observer;
    const revealed = [];

    // Wait a frame so the new route has rendered and its own animations are applied.
    const frame = requestAnimationFrame(() => {
      const main = document.querySelector("main");
      if (!main) return;

      observer = new IntersectionObserver(
        entries =>
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-in");
            observer.unobserve(entry.target);
          }),
        { rootMargin: "0px 0px -10% 0px" }
      );

      collectTargets(main).forEach(([el, delay]) => {
        el.classList.add("ft-reveal");
        if (delay) el.style.setProperty("--ft-reveal-delay", `${delay}ms`);
        revealed.push(el);
        observer.observe(el);
      });
    });

    return () => {
      cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      revealed.forEach(el => {
        el.classList.remove("ft-reveal", "is-in");
        el.style.removeProperty("--ft-reveal-delay");
      });
    };
  }, [pathname]);

  return null;
}
